/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, {
	forwardRef,
	memo,
	type RefObject,
	useCallback,
	useLayoutEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from 'react';

import { cssMap, jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import { useMergeRefs } from 'use-callback-ref';

import { fg } from '@atlaskit/platform-feature-flags';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import { TAB_GAP, TAB_GAP_OLD } from '@atlassian/navigation-kit-common';
import { TabProvider } from '@atlassian/navigation-kit-ui-tab';

import ResponsiveNav from '../../common/ui/responsive-nav';
import TabWrapper from '../../common/ui/tab-wrapper';

import { MoreMenuContents } from './more-menu-contents';
import { MoreTrigger } from './more-trigger';

export type Props = {
	addTab?: React.ReactNode;
	id?: string;
	label: string;
	onMoreClick?: (context: { overflowingTabCount: number }) => void;
	selectedTabId?: string | null;
	tabs: {
		id: string;
		tab: React.ReactNode;
	}[];
};

const calculateOverflowTabs = (
	tabContainer: RefObject<HTMLDivElement>,
	showMoreButton: () => () => void,
	selectedTabIndex: number,
) => {
	const TAB_GAP_VALUE = fg('blu-6305_tab_item_styling_in_horizontal_navigation')
		? TAB_GAP
		: TAB_GAP_OLD;

	const overflowTabs: Set<number> = new Set();

	if (tabContainer.current == null) {
		return overflowTabs;
	}

	// All the tabs fit in the container, no need to calculate overflow.
	if (tabContainer.current.scrollWidth <= tabContainer.current.offsetWidth) {
		return overflowTabs;
	}

	// Show the more button as it takes up space in the tab list. This ensures we
	// calculate the tab container width correctly below.
	const resetMoreButtonStyles = showMoreButton();

	// Calculate the tab container width by subtracting padding from the tab container offset width.
	const tabContainerComputedStyle = window.getComputedStyle(tabContainer.current);
	const tabContainerPadding =
		parseFloat(tabContainerComputedStyle.paddingLeft) +
		parseFloat(tabContainerComputedStyle.paddingRight);
	const tabContainerWidth = tabContainer.current.offsetWidth - tabContainerPadding;

	const tabs = tabContainer.current.children as HTMLCollectionOf<HTMLElement>;
	const selectedTabWidth = tabs[selectedTabIndex]?.offsetWidth || 0;
	const selectedTabGap = selectedTabIndex === -1 ? 0 : TAB_GAP_VALUE;

	const availableWidth = tabContainerWidth - selectedTabWidth - selectedTabGap;

	let tabsWidth = 0;
	let gapCount = 0;

	for (let i = 0; i < tabs.length; i++) {
		// Skip the selected tab, as it should always be visible. Its width and gap are already accounted for above.
		if (i === selectedTabIndex) {
			continue;
		}

		tabsWidth += tabs[i].offsetWidth;

		if (tabsWidth > availableWidth - TAB_GAP_VALUE * gapCount) {
			overflowTabs.add(i);
		}

		gapCount++;
	}

	resetMoreButtonStyles();

	return overflowTabs;
};

type OverflowAction =
	| { type: 'SET_MUST_RECALCULATE' }
	| { type: 'SET_OVERFLOW_TABS'; payload: Set<number> };

type OverflowState = {
	mustRecalculateOverflowTabs: boolean;
	overflowTabs: Set<number>;
};

const initialOverflowState: OverflowState = {
	mustRecalculateOverflowTabs: false,
	overflowTabs: new Set(),
};

const overflowReducer = (state: OverflowState, action: OverflowAction): OverflowState => {
	switch (action.type) {
		case 'SET_MUST_RECALCULATE':
			return {
				...state,
				mustRecalculateOverflowTabs: true,
				overflowTabs: new Set(),
			};
		case 'SET_OVERFLOW_TABS':
			return {
				...state,
				mustRecalculateOverflowTabs: false,
				overflowTabs: action.payload,
			};
		default:
			return state;
	}
};

const TabList = forwardRef(
	(
		{ addTab, id, label, onMoreClick, selectedTabId, tabs }: Props,
		ref: React.Ref<HTMLDivElement>,
	) => {
		const [isMoreOpen, setIsMoreOpen] = useState(false);
		const navRef = useRef<HTMLElement>(null);
		const internalTabContainerRef = useRef<HTMLDivElement>(null);
		const tabContainerRef = useMergeRefs([ref, internalTabContainerRef]);
		const moreButtonWrapperRef = useRef<HTMLDivElement>(null);
		const [{ mustRecalculateOverflowTabs, overflowTabs }, dispatch] = useReducer(
			overflowReducer,
			initialOverflowState,
		);

		const selectedTabIndex = tabs.findIndex(({ id }) => id === selectedTabId);
		const overflowingTabCount = overflowTabs.size;
		const hasOverflowTabs = overflowingTabCount > 0;

		const debouncedCalculateOverflowTabs = useMemo(
			() =>
				debounce(
					() => {
						dispatch({ type: 'SET_MUST_RECALCULATE' });
					},
					300,
					{ leading: true },
				),
			[],
		);

		useLayoutEffect(() => {
			debouncedCalculateOverflowTabs();
		}, [debouncedCalculateOverflowTabs, selectedTabId, tabs]);

		useLayoutEffect(() => {
			if (mustRecalculateOverflowTabs) {
				const showMoreButton = () => {
					if (moreButtonWrapperRef.current != null) {
						moreButtonWrapperRef.current.style.display = 'block';
					}

					return () => {
						if (moreButtonWrapperRef.current != null) {
							moreButtonWrapperRef.current.removeAttribute('style');
						}
					};
				};

				dispatch({
					type: 'SET_OVERFLOW_TABS',
					payload: calculateOverflowTabs(internalTabContainerRef, showMoreButton, selectedTabIndex),
				});
			}
		}, [mustRecalculateOverflowTabs, selectedTabIndex]);

		useLayoutEffect(() => {
			const resizeObserver = new ResizeObserver(debouncedCalculateOverflowTabs);

			if (navRef.current) {
				resizeObserver.observe(navRef.current);
			}

			return () => {
				resizeObserver.disconnect();
			};
		}, [debouncedCalculateOverflowTabs]);

		const onMoreTabClick = useCallback(() => {
			setIsMoreOpen((prev) => !prev);
			if (onMoreClick) {
				onMoreClick({ overflowingTabCount });
			}
		}, [onMoreClick, overflowingTabCount]);

		const { overflowingTabs, visibleTabs } = useMemo(() => {
			const visibleTabs: React.ReactNode[] = [];
			const overflowingTabs: React.ReactNode[] = [];

			tabs.forEach(({ tab, id }, index) => {
				const tabContent = (
					<TabProvider
						id={id}
						isOverflowing={overflowTabs.has(index)}
						isSelected={id === selectedTabId}
						key={id}
					>
						{tab}
					</TabProvider>
				);

				if (overflowTabs.has(index)) {
					overflowingTabs.push(tabContent);
				} else {
					visibleTabs.push(tabContent);
				}
			});

			return { overflowingTabs, visibleTabs };
		}, [overflowTabs, selectedTabId, tabs]);

		const closeMore = useCallback(() => setIsMoreOpen(false), []);

		const renderPopupContent = useCallback(() => {
			return (
				<MoreMenuContents close={closeMore} selectedTabId={selectedTabId}>
					{overflowingTabs}
				</MoreMenuContents>
			);
		}, [closeMore, overflowingTabs, selectedTabId]);

		const renderPopupTrigger = useCallback(
			(triggerProps: TriggerProps) => (
				<div
					css={[styles.moreButtonWrapper, !hasOverflowTabs && styles.moreButtonWrapperHidden]}
					ref={moreButtonWrapperRef}
				>
					<MoreTrigger
						onClick={onMoreTabClick}
						count={overflowingTabCount}
						{...triggerProps}
						isOpen={isMoreOpen}
					/>
				</div>
			),
			[hasOverflowTabs, isMoreOpen, onMoreTabClick, overflowingTabCount],
		);

		return (
			<ResponsiveNav id={id} ref={navRef} label={label}>
				<TabWrapper ref={tabContainerRef}>{visibleTabs}</TabWrapper>
				<Popup
					content={renderPopupContent}
					isOpen={isMoreOpen}
					onClose={closeMore}
					placement="bottom-end"
					testId="navigation-kit-ui-tab-list.ui.more-popup"
					trigger={renderPopupTrigger}
				/>
				{addTab && (
					<div
						css={[
							styles.addTabWrapper,
							fg('blu-6305_tab_item_styling_in_horizontal_navigation') &&
								styles.addTabWrapperWithPadding,
						]}
					>
						{addTab}
					</div>
				)}
			</ResponsiveNav>
		);
	},
);

const styles = cssMap({
	addTabWrapper: {
		display: 'flex',
		flex: 'none',
	},
	// merge to main styling and remove it when cleaning blu-6305_tab_item_styling_in_horizontal_navigation
	addTabWrapperWithPadding: {
		paddingBlock: token('space.050'),
	},
	moreButtonWrapper: {
		flex: 'none',
	},
	moreButtonWrapperHidden: {
		display: 'none',
	},
});

export default memo(TabList);
